import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Box, Button, Dialog, DialogTitle, DialogContent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { supabase } from '../../lib/supabaseClient';
import { format, startOfMonth, endOfMonth } from 'date-fns';

const ProductionAnalytics = () => {
  const [productions, setProductions] = useState([]);
  const [filteredProductions, setFilteredProductions] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [isProjectsDialogOpen, setIsProjectsDialogOpen] = useState(false);

  useEffect(() => {
    fetchProductionsAndProjects();
  }, []);

  const fetchProductionsAndProjects = async () => {
    // Fetch productions and projects as before
    const { data: productionsData, error: productionsError } = await supabase
      .from('production')
      .select('*')
      .order('created_at', { ascending: false });

    if (productionsError) {
      console.error('Error fetching productions:', productionsError);
      return;
    }

    const { data: projectsData, error: projectsError } = await supabase
      .from('project')
      .select('*')
      .order('created_at', { ascending: false });

    if (projectsError) {
      console.error('Error fetching projects:', projectsError);
      return;
    }

    // Group productions by Name and combine their projects
    const groupedProductions = productionsData.reduce((acc, prod) => {
      if (!acc[prod.Name]) {
        acc[prod.Name] = {
          ...prod,
          projects: [],
          created_at: prod.created_at // Keep the earliest creation date
        };
      }
      
      // Add projects for this production house
      const relatedProjects = projectsData.filter(proj => proj.project_id === prod.project_id);
      acc[prod.Name].projects.push(...relatedProjects);
      
      // Update creation date if this production is older
      if (new Date(prod.created_at) < new Date(acc[prod.Name].created_at)) {
        acc[prod.Name].created_at = prod.created_at;
      }

      return acc;
    }, {});

    // Convert grouped object back to array
    const productionsWithProjects = Object.values(groupedProductions).map(prod => ({
      ...prod,
      projectCount: prod.projects.length
    }));

    setProductions(productionsWithProjects);
    setFilteredProductions(productionsWithProjects);
    processGraphData(productionsWithProjects);
  };

  const processGraphData = (productionsData) => {
    const monthlyData = productionsData.reduce((acc, prod) => {
      const month = format(new Date(prod.created_at), 'MMM yyyy');
      acc[month] = (acc[month] || 0) + 1;
      return acc;
    }, {});

    const graphData = Object.entries(monthlyData).map(([month, count]) => ({
      month,
      productions: count,
    }));

    setGraphData(graphData);
  };

  const handleSearch = () => {
    let filtered = productions;

    if (searchName) {
      filtered = filtered.filter(prod =>
        prod.Name?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (startDate) {
      filtered = filtered.filter(prod =>
        new Date(prod.created_at) >= startOfMonth(startDate)
      );
    }

    if (endDate) {
      filtered = filtered.filter(prod =>
        new Date(prod.created_at) <= endOfMonth(endDate)
      );
    }

    setFilteredProductions(filtered);
  };

  const handleProjectsClick = (projects) => {
    setSelectedProjects(projects);
    setIsProjectsDialogOpen(true);
  };

  return (
    <div className="production-analytics">
      <h2>Production House Analytics</h2>

      {/* Bar Chart */}
      <div style={{ height: '400px', marginBottom: '2rem' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="productions" fill="#8884d8" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Search Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by Production House Name"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="From Date"
            value={startDate}
            onChange={setStartDate}
          />
          <DatePicker
            label="To Date"
            value={endDate}
            onChange={setEndDate}
          />
        </LocalizationProvider>
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Box>

      {/* Productions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S. No.</TableCell>
              <TableCell>Creation Date</TableCell>
              <TableCell>Production House Name</TableCell>
              <TableCell>No. of Projects Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProductions.map((prod, index) => (
              <TableRow key={prod.prod_id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  {format(new Date(prod.created_at), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>{prod.Name}</TableCell>
                <TableCell>
                  <Button
                    onClick={() => handleProjectsClick(prod.projects)}
                    variant="text"
                    color="primary"
                  >
                    {prod.projectCount}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Projects Dialog */}
      <Dialog
        open={isProjectsDialogOpen}
        onClose={() => setIsProjectsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Projects</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S. No.</TableCell>
                  <TableCell>Creation Date</TableCell>
                  <TableCell>Project Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedProjects.map((project, index) => (
                  <TableRow key={project.project_id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {format(new Date(project.created_at), 'dd/MM/yyyy')}
                    </TableCell>
                    <TableCell>{project.project_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductionAnalytics; 